import { ErrorLike } from "@apollo/client";
import { Button, ButtonText } from "@repo/components/src/ui/button";
import { Icon } from "@repo/components/src/ui/icon";
import { Image } from "@repo/components/src/ui/image";
import { Text } from "@repo/components/src/ui/text";
import { Link } from "expo-router";
import { AlertCircle } from "lucide-react-native";
import React, { ComponentProps } from "react";
import { View } from "react-native";
import RNAnimated from "react-native-reanimated";

type AuthLoadingScreenProps = {
  message: string;
  error?: ErrorLike;
  // Logo shown while loading when no `icon` is provided. Passed per-app so this
  // shared screen doesn't depend on any app-local asset.
  logo?: ComponentProps<typeof Image>["source"];
} & (
  | {
      icon?: undefined;
      iconColor?: never;
    }
  | {
      icon: ComponentProps<typeof Icon>["as"];
      iconColor: string;
    }
);

const PulsingOrb = ({
  icon,
  iconColor,
}: {
  icon: ComponentProps<typeof Icon>["as"];
  iconColor: string;
}) => {
  const orbColor = iconColor;

  return (
    <View className="items-center justify-center w-24 h-24">
      <RNAnimated.View
        style={{
          position: "absolute",
          width: 96,
          height: 96,
          borderRadius: 48,
          backgroundColor: orbColor,
          animationName: {
            from: {
              opacity: 0.3,
              transform: [{ scale: 0.85 }],
            },
            to: {
              opacity: 0.6,
              transform: [{ scale: 1 }],
            },
          },
          animationDuration: "1.2s",
          animationTimingFunction: "ease-in-out",
          animationIterationCount: "infinite",
          animationDirection: "alternate",
        }}
      />
      <View
        className="items-center justify-center"
        style={{
          width: 64,
          height: 64,
          borderRadius: 32,
          backgroundColor: orbColor,
        }}
      >
        <Icon
          as={icon}
          className="text-white fill-white stroke-none"
          size="3xl"
        />
      </View>
    </View>
  );
};

const ProgressBar = () => {
  return (
    <View className="overflow-hidden rounded-full w-52 h-1 bg-background-200">
      <RNAnimated.View
        className="bg-brand-500"
        style={{
          height: 3,
          borderRadius: 2,
          animationName: {
            from: {
              width: "0%",
            },
            to: {
              width: "100%",
            },
          },
          animationDuration: "6s",
          animationTimingFunction: "ease-in-out",
          animationIterationCount: "infinite",
          animationDirection: "normal",
        }}
      />
    </View>
  );
};

const AuthLoadingScreen = ({
  message,
  error,
  icon,
  iconColor,
  logo,
}: AuthLoadingScreenProps) => {
  return (
    <View
      className="flex-1 items-center justify-center bg-background-50"
      style={
        {
          minHeight: "100vh",
        } as never
      }
    >
      <View className="items-center gap-10">
        {error ? (
          <View className="items-center gap-6" style={{ maxWidth: 340 }}>
            <View className="items-center justify-center w-16 h-16 rounded-full bg-error-50">
              <Icon as={AlertCircle} className="w-8 h-8 text-error-500" />
            </View>
            <View className="items-center gap-2">
              <Text className="text-xl font-semibold text-center text-typography-900">
                Algo salio mal
              </Text>
              <Text
                className="text-sm text-center text-typography-400"
                style={{ lineHeight: 20 }}
              >
                {error.message}
              </Text>
            </View>
            <Link href={{ pathname: "/" }} asChild replace>
              <Button
                action="brand"
                variant="solid"
                size="lg"
                className="w-full"
                rounded="xl"
              >
                <ButtonText>Volver al inicio</ButtonText>
              </Button>
            </Link>
          </View>
        ) : (
          <View className="items-center gap-8">
            {!!icon ? (
              <PulsingOrb icon={icon} iconColor={iconColor} />
            ) : (
              !!logo && (
                <Image
                  source={logo}
                  className="w-48 aspect-auto"
                  contentFit="contain"
                  alt="Montu"
                />
              )
            )}
            <View className="items-center gap-4">
              <Text className="text-base font-medium text-typography-700">
                {message}
              </Text>
              <ProgressBar />
            </View>
          </View>
        )}
      </View>
    </View>
  );
};

export default React.memo(AuthLoadingScreen);
